import type { HubDefinition, StandaloneAppDefinition, ToolDefinition } from './types';

type LocalToolInput = Omit<
  ToolDefinition,
  | 'processingMode'
  | 'sensitiveInput'
  | 'supportsCopy'
  | 'supportsDownload'
  | 'supportsExamples'
  | 'supportsFavorites'
>;

function localTool(
  definition: LocalToolInput &
    Partial<
      Pick<
        ToolDefinition,
        | 'processingMode'
        | 'sensitiveInput'
        | 'supportsCopy'
        | 'supportsDownload'
        | 'supportsExamples'
        | 'supportsFavorites'
      >
    >
): ToolDefinition {
  return {
    processingMode: 'local',
    sensitiveInput: false,
    supportsCopy: true,
    supportsDownload: false,
    supportsExamples: false,
    supportsFavorites: true,
    ...definition
  };
}

/** Canonical metadata for every active Round 2 hub and tool. */
export const HUB_REGISTRY: readonly HubDefinition[] = [
  {
    id: 'calculator',
    name: 'Calculator & Converter',
    description: 'Everyday math, business and finance calculations, and unit conversions.',
    path: '/apps/calculator',
    icon: 'dx-icon-formula',
    minRole: 'AssistantUser',
    status: 'ready',
    tools: [
      localTool({
        id: 'calculator-standard',
        name: 'Calculator',
        shortName: 'Standard',
        description: 'Standard and scientific calculations with keyboard input and history.',
        hubId: 'calculator',
        tabId: 'standard',
        path: '/apps/calculator/standard',
        keywords: ['math', 'scientific', 'history', 'expression'],
        aliases: ['standard calculator', 'scientific calculator'],
        icon: 'dx-icon-formula',
        status: 'ready'
      }),
      localTool({
        id: 'calculator-units',
        name: 'Unit Converter',
        shortName: 'Units',
        description: 'Convert common measurement, storage, energy, pressure, and angle units.',
        hubId: 'calculator',
        tabId: 'units',
        path: '/apps/calculator/units',
        keywords: [
          'convert',
          'length',
          'weight',
          'area',
          'speed',
          'temperature',
          'pressure',
          'energy',
          'power',
          'angle',
          'storage'
        ],
        aliases: [
          'measurement converter',
          'data size converter',
          'square feet to square meters',
          'mph to kmh',
          'kilowatt hours',
          'degrees to radians'
        ],
        icon: 'dx-icon-repeat',
        status: 'ready'
      }),
      localTool({
        id: 'calculator-business',
        name: 'Business Calculators',
        shortName: 'Business',
        description: 'Configurable payroll, margin, tax, discount, and unit-economics calculators.',
        hubId: 'calculator',
        tabId: 'business',
        path: '/apps/calculator/business',
        keywords: ['payroll', 'overtime', 'margin', 'markup', 'tax', 'discount', 'cac', 'ltv'],
        aliases: ['business math', 'break even', 'late fee'],
        icon: 'dx-icon-money',
        status: 'ready'
      }),
      localTool({
        id: 'calculator-finance',
        name: 'Finance Calculators',
        shortName: 'Finance',
        description: 'Loans, amortization, simple interest, compound growth, and savings goals.',
        hubId: 'calculator',
        tabId: 'finance',
        path: '/apps/calculator/finance',
        keywords: ['loan', 'amortization', 'interest', 'savings', 'compound'],
        aliases: ['mortgage calculator', 'compound interest', 'savings goal'],
        icon: 'dx-icon-chart',
        status: 'ready',
        supportsDownload: true
      })
    ]
  },
  {
    id: 'time-date',
    name: 'Time & Date',
    description: 'World clocks, timers, epoch conversion, date math, and timezone conversion.',
    path: '/apps/time-date',
    icon: 'dx-icon-clock',
    minRole: 'AssistantUser',
    status: 'ready',
    tools: [
      localTool({
        id: 'time-world-clock',
        name: 'World Clock',
        shortName: 'World Clock',
        description: 'Track local time and offsets for saved locations.',
        hubId: 'time-date',
        tabId: 'world-clock',
        path: '/apps/time-date/world-clock',
        keywords: ['timezone', 'offset', 'dst', 'location'],
        aliases: ['timezone clock', 'international time'],
        icon: 'dx-icon-globe',
        status: 'ready'
      }),
      localTool({
        id: 'time-stopwatch-timer',
        name: 'Stopwatch & Timer',
        shortName: 'Timer',
        description: 'Track elapsed time, laps, and countdowns.',
        hubId: 'time-date',
        tabId: 'stopwatch-timer',
        path: '/apps/time-date/stopwatch-timer',
        keywords: ['elapsed', 'countdown', 'laps', 'alarm'],
        aliases: ['stop watch', 'countdown timer'],
        icon: 'dx-icon-runner',
        status: 'ready',
        supportsDownload: true
      }),
      localTool({
        id: 'time-epoch',
        name: 'Epoch Converter',
        shortName: 'Epoch',
        description: 'Convert Unix timestamps, ISO 8601, UTC, and local date parts.',
        hubId: 'time-date',
        tabId: 'epoch',
        path: '/apps/time-date/epoch',
        keywords: ['unix', 'timestamp', 'milliseconds', 'iso 8601'],
        aliases: ['unix time', 'date converter'],
        icon: 'dx-icon-event',
        status: 'ready'
      }),
      localTool({
        id: 'time-date-calc',
        name: 'Date Calculator',
        shortName: 'Date Math',
        description: 'Calculate date differences, ages, business days, and recurring dates.',
        hubId: 'time-date',
        tabId: 'date-calc',
        path: '/apps/time-date/date-calc',
        keywords: ['age', 'business days', 'weekday', 'iso week', 'day of year'],
        aliases: ['date math', 'days between dates'],
        icon: 'dx-icon-event',
        status: 'ready'
      }),
      localTool({
        id: 'time-timezone-converter',
        name: 'Time-Zone Converter',
        shortName: 'Time Zones',
        description: 'Convert a local date and time to multiple daylight-saving-aware zones.',
        hubId: 'time-date',
        tabId: 'timezone-converter',
        path: '/apps/time-date/timezone-converter',
        keywords: ['timezone', 'dst', 'meeting', 'offset'],
        aliases: ['time zone converter', 'meeting time converter'],
        icon: 'dx-icon-clock',
        status: 'ready'
      })
    ]
  },
  {
    id: 'data',
    name: 'Data Tools',
    description: 'Format, validate, convert, query, and export structured data.',
    path: '/apps/data',
    icon: 'dx-icon-codeblock',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'data-format',
        name: 'Formatter & Validator',
        shortName: 'Format',
        description: 'Format and validate JSON, XML, YAML, and CSV.',
        hubId: 'data',
        tabId: 'format',
        path: '/apps/data/format',
        keywords: ['json', 'xml', 'yaml', 'csv', 'prettify', 'minify'],
        aliases: ['json formatter', 'xml validator'],
        icon: 'dx-icon-codeblock',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'data-convert',
        name: 'Data Conversion',
        shortName: 'Convert',
        description: 'Convert among JSON, CSV, XML, and YAML with semantic-loss warnings.',
        hubId: 'data',
        tabId: 'convert',
        path: '/apps/data/convert',
        keywords: ['json', 'csv', 'xml', 'yaml', 'export'],
        aliases: ['convert json csv', 'data converter'],
        icon: 'dx-icon-repeat',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'data-schema',
        name: 'Schema & Query',
        shortName: 'Schema',
        description: 'Validate JSON Schema and test JSONPath and XPath expressions.',
        hubId: 'data',
        tabId: 'schema',
        path: '/apps/data/schema',
        keywords: ['json schema', 'jsonpath', 'xpath', 'query'],
        aliases: ['schema validator', 'path tester'],
        icon: 'dx-icon-search',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      }),
      localTool({
        id: 'data-markdown',
        name: 'Markdown & HTML',
        shortName: 'Markdown',
        description: 'Convert Markdown and HTML with a sanitized preview.',
        hubId: 'data',
        tabId: 'markdown',
        path: '/apps/data/markdown',
        keywords: ['markdown', 'html', 'sanitize', 'preview'],
        aliases: ['markdown to html', 'html to markdown'],
        icon: 'dx-icon-doc',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'data-url',
        name: 'URL & Query String',
        shortName: 'URL',
        description: 'Parse and rebuild URL components and query parameters locally.',
        hubId: 'data',
        tabId: 'url',
        path: '/apps/data/url',
        keywords: ['url', 'query string', 'parameters', 'encode'],
        aliases: ['url parser', 'query builder'],
        icon: 'dx-icon-link',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      }),
      localTool({
        id: 'data-sql',
        name: 'SQL Formatter',
        shortName: 'SQL',
        description: 'Format and minify SQL without executing it.',
        hubId: 'data',
        tabId: 'sql',
        path: '/apps/data/sql',
        keywords: ['sql', 'format', 'minify', 'dialect'],
        aliases: ['query formatter'],
        icon: 'dx-icon-codeblock',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true
      })
    ]
  },
  {
    id: 'text',
    name: 'Text & Pattern Tools',
    description: 'Test regular expressions, compare content, and transform text.',
    path: '/apps/text',
    icon: 'dx-icon-find',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'text-regex',
        name: 'Regex Tester',
        shortName: 'Regex',
        description: 'Test JavaScript regular expressions and inspect matches and groups.',
        hubId: 'text',
        tabId: 'regex',
        path: '/apps/text/regex',
        keywords: ['regex', 'pattern', 'match', 'capture', 'flags'],
        aliases: ['regular expression tester'],
        icon: 'dx-icon-find',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      }),
      localTool({
        id: 'text-regex-reference',
        name: 'Regex Reference',
        shortName: 'Reference',
        description: 'Search JavaScript regex syntax, recipes, and engine limitations.',
        hubId: 'text',
        tabId: 'regex-reference',
        path: '/apps/text/regex-reference',
        keywords: ['character class', 'quantifier', 'anchor', 'lookaround'],
        aliases: ['regex cheat sheet'],
        icon: 'dx-icon-doc',
        status: 'ready',
        supportsExamples: true
      }),
      localTool({
        id: 'text-regex-replace',
        name: 'Regex Replace',
        shortName: 'Replace',
        description: 'Preview first or global replacements and capture substitutions.',
        hubId: 'text',
        tabId: 'regex-replace',
        path: '/apps/text/regex-replace',
        keywords: ['replace', 'substitution', 'capture group'],
        aliases: ['regex substitution'],
        icon: 'dx-icon-repeat',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      }),
      localTool({
        id: 'text-diff',
        name: 'Text Diff',
        shortName: 'Text Diff',
        description: 'Compare text in inline or side-by-side views.',
        hubId: 'text',
        tabId: 'text-diff',
        path: '/apps/text/text-diff',
        keywords: ['diff', 'compare', 'lines', 'whitespace'],
        aliases: ['text compare'],
        icon: 'dx-icon-fields',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true
      }),
      localTool({
        id: 'text-json-diff',
        name: 'JSON Diff',
        shortName: 'JSON Diff',
        description: 'Compare JSON structures and report paths to every difference.',
        hubId: 'text',
        tabId: 'json-diff',
        path: '/apps/text/json-diff',
        keywords: ['json', 'diff', 'compare', 'path', 'array'],
        aliases: ['structural json compare'],
        icon: 'dx-icon-codeblock',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'text-utilities',
        name: 'Text Utilities',
        shortName: 'Utilities',
        description: 'Change case, count, sort, deduplicate, trim, and affix text.',
        hubId: 'text',
        tabId: 'utilities',
        path: '/apps/text/utilities',
        keywords: ['case', 'slug', 'count', 'sort', 'deduplicate', 'trim'],
        aliases: ['text transform', 'word count'],
        icon: 'dx-icon-fontsize',
        status: 'ready',
        sensitiveInput: true
      })
    ]
  },
  {
    id: 'encoding',
    name: 'Encoding & Character Tools',
    description: 'Encode data, convert number bases, hash content, and inspect characters.',
    path: '/apps/encoding',
    icon: 'dx-icon-key',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'encoding-base64',
        name: 'Base64 & URL Encoding',
        shortName: 'Base64',
        description: 'Encode and decode Base64, Base64URL, URLs, and small files.',
        hubId: 'encoding',
        tabId: 'base64',
        path: '/apps/encoding/base64',
        keywords: ['base64', 'base64url', 'url', 'utf-8'],
        aliases: ['encode decode'],
        icon: 'dx-icon-codeblock',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true
      }),
      localTool({
        id: 'encoding-number-base',
        name: 'Number Base Conversion',
        shortName: 'Number Base',
        description: 'Convert binary, octal, decimal, and hexadecimal values safely.',
        hubId: 'encoding',
        tabId: 'number-base',
        path: '/apps/encoding/number-base',
        keywords: ['binary', 'octal', 'decimal', 'hexadecimal', 'bigint'],
        aliases: ['base converter', 'hex converter'],
        icon: 'dx-icon-repeat',
        status: 'ready'
      }),
      localTool({
        id: 'encoding-hash',
        name: 'Hash & Checksum',
        shortName: 'Hash',
        description: 'Hash text or files and compare expected checksums.',
        hubId: 'encoding',
        tabId: 'hash',
        path: '/apps/encoding/hash',
        keywords: ['md5', 'sha-1', 'sha-256', 'sha-384', 'sha-512', 'checksum'],
        aliases: ['file hash', 'digest'],
        icon: 'dx-icon-key',
        status: 'ready',
        sensitiveInput: true
      }),
      localTool({
        id: 'encoding-character',
        name: 'Character Inspector',
        shortName: 'Character',
        description: 'Inspect Unicode code points, bytes, entities, and invisible characters.',
        hubId: 'encoding',
        tabId: 'character',
        path: '/apps/encoding/character',
        keywords: ['ascii', 'unicode', 'utf-8', 'entity', 'invisible'],
        aliases: ['unicode inspector', 'code point'],
        icon: 'dx-icon-fontsize',
        status: 'ready'
      })
    ]
  },
  {
    id: 'network',
    name: 'Network Tools',
    description: 'Calculate networks, inspect addresses, and perform approved lookups.',
    path: '/apps/network',
    icon: 'dx-icon-globe',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'network-subnet',
        name: 'Subnet & CIDR',
        shortName: 'Subnet',
        description: 'Calculate IPv4 and IPv6 ranges, masks, and classifications.',
        hubId: 'network',
        tabId: 'subnet',
        path: '/apps/network/subnet',
        keywords: ['cidr', 'netmask', 'subnet', 'ip range', 'ipv4', 'ipv6'],
        aliases: ['cidr calculator'],
        icon: 'dx-icon-globe',
        status: 'ready'
      }),
      localTool({
        id: 'network-ip-lookup',
        name: 'IP Lookup',
        shortName: 'IP Lookup',
        description: 'Look up approved approximate geolocation and network ownership data.',
        hubId: 'network',
        tabId: 'ip-lookup',
        path: '/apps/network/ip-lookup',
        keywords: ['ip', 'geolocation', 'asn', 'isp'],
        aliases: ['ip info'],
        icon: 'dx-icon-map',
        status: 'ready',
        processingMode: 'provider',
        sensitiveInput: true
      }),
      localTool({
        id: 'network-dns',
        name: 'DNS Lookup',
        shortName: 'DNS',
        description: 'Resolve supported DNS record types through an approved service.',
        hubId: 'network',
        tabId: 'dns',
        path: '/apps/network/dns',
        keywords: ['a', 'aaaa', 'cname', 'mx', 'txt', 'ns', 'soa', 'ptr'],
        aliases: ['domain lookup', 'reverse dns'],
        icon: 'dx-icon-search',
        status: 'ready',
        processingMode: 'provider',
        sensitiveInput: true
      }),
      localTool({
        id: 'network-whois',
        name: 'WHOIS / RDAP',
        shortName: 'WHOIS',
        description: 'Query standards-based domain or address registration data.',
        hubId: 'network',
        tabId: 'whois',
        path: '/apps/network/whois',
        keywords: ['whois', 'rdap', 'registrar', 'nameserver'],
        aliases: ['domain registration'],
        icon: 'dx-icon-info',
        status: 'ready',
        processingMode: 'provider',
        sensitiveInput: true
      }),
      localTool({
        id: 'network-ip-inspector',
        name: 'IP Inspector',
        shortName: 'IP Inspector',
        description: 'Normalize and classify IP addresses locally.',
        hubId: 'network',
        tabId: 'ip-inspector',
        path: '/apps/network/ip-inspector',
        keywords: ['private', 'public', 'reserved', 'loopback', 'multicast'],
        aliases: ['ip classifier'],
        icon: 'dx-icon-info',
        status: 'ready'
      })
    ]
  },
  {
    id: 'developer',
    name: 'Developer Tools',
    description: 'Inspect schedules, protocols, tokens, certificates, environments, and agents.',
    path: '/apps/developer',
    icon: 'dx-icon-codeblock',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'developer-cron',
        name: 'Cron Tools',
        shortName: 'Cron',
        description: 'Explain, validate, and preview supported cron expressions.',
        hubId: 'developer',
        tabId: 'cron',
        path: '/apps/developer/cron',
        keywords: ['cron', 'schedule', 'next run', 'timezone'],
        aliases: ['cron helper', 'cron expression'],
        icon: 'dx-icon-clock',
        status: 'ready',
        supportsExamples: true
      }),
      localTool({
        id: 'developer-http-status',
        name: 'HTTP Status Codes',
        shortName: 'HTTP Status',
        description: 'Search standards-aligned HTTP status code guidance.',
        hubId: 'developer',
        tabId: 'http-status',
        path: '/apps/developer/http-status',
        keywords: ['http', 'status', 'response', 'retry', 'cache'],
        aliases: ['status code reference'],
        icon: 'dx-icon-info',
        status: 'ready',
        supportsExamples: true
      }),
      localTool({
        id: 'developer-jwt',
        name: 'JWT Decoder',
        shortName: 'JWT',
        description: 'Decode token sections and claims locally without implying verification.',
        hubId: 'developer',
        tabId: 'jwt',
        path: '/apps/developer/jwt',
        keywords: ['jwt', 'token', 'claims', 'exp', 'iat', 'nbf'],
        aliases: ['token decoder'],
        icon: 'dx-icon-key',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      }),
      localTool({
        id: 'developer-certificates',
        name: 'Certificate Tools',
        shortName: 'Certificates',
        description: 'Parse supplied PEM certificates locally and report validity details.',
        hubId: 'developer',
        tabId: 'certificates',
        path: '/apps/developer/certificates',
        keywords: ['pem', 'x509', 'certificate', 'san', 'fingerprint'],
        aliases: ['certificate decoder', 'ssl certificate'],
        icon: 'dx-icon-key',
        status: 'ready',
        sensitiveInput: true
      }),
      localTool({
        id: 'developer-env',
        name: 'Environment File Tools',
        shortName: '.env',
        description: 'Format, compare, mask, and sanitize environment files locally.',
        hubId: 'developer',
        tabId: 'env',
        path: '/apps/developer/env',
        keywords: ['env', 'environment', 'variables', 'mask', 'example'],
        aliases: ['dotenv formatter', 'env compare'],
        icon: 'dx-icon-codeblock',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'developer-user-agent',
        name: 'User-Agent Parser',
        shortName: 'User Agent',
        description: 'Parse user-agent hints with an explicit spoofing disclaimer.',
        hubId: 'developer',
        tabId: 'user-agent',
        path: '/apps/developer/user-agent',
        keywords: ['browser', 'operating system', 'device', 'bot', 'crawler'],
        aliases: ['ua parser'],
        icon: 'dx-icon-runner',
        status: 'ready',
        sensitiveInput: true,
        supportsExamples: true
      })
    ]
  },
  {
    id: 'generators',
    name: 'Generators',
    description: 'Generate secure identifiers, synthetic data, placeholder content, and QR codes.',
    path: '/apps/generators',
    icon: 'dx-icon-add',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'generators-uuid-password',
        name: 'UUID & Password',
        shortName: 'UUID & Password',
        description: 'Generate UUIDs, passwords, and passphrases with secure browser randomness.',
        hubId: 'generators',
        tabId: 'uuid-password',
        path: '/apps/generators/uuid-password',
        keywords: ['uuid', 'password', 'passphrase', 'entropy', 'random'],
        aliases: ['guid generator', 'password generator'],
        icon: 'dx-icon-key',
        status: 'ready',
        sensitiveInput: true
      }),
      localTool({
        id: 'generators-mock-data',
        name: 'Mock Data Generator',
        shortName: 'Mock Data',
        description: 'Generate bounded, reproducible synthetic records from a custom schema.',
        hubId: 'generators',
        tabId: 'mock-data',
        path: '/apps/generators/mock-data',
        keywords: ['mock', 'synthetic', 'seed', 'json', 'csv', 'sql'],
        aliases: ['fake data generator', 'test data'],
        icon: 'dx-icon-dataarea',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true,
        supportsExamples: true
      }),
      localTool({
        id: 'generators-placeholder',
        name: 'Placeholder Content',
        shortName: 'Placeholder',
        description: 'Generate controlled placeholder text, lists, tables, and UI copy.',
        hubId: 'generators',
        tabId: 'placeholder',
        path: '/apps/generators/placeholder',
        keywords: ['lorem ipsum', 'sentences', 'paragraphs', 'headings'],
        aliases: ['dummy text', 'placeholder text'],
        icon: 'dx-icon-doc',
        status: 'ready',
        supportsDownload: true
      }),
      localTool({
        id: 'generators-qr',
        name: 'QR Tools',
        shortName: 'QR',
        description: 'Generate QR codes and locally decode approved uploaded images.',
        hubId: 'generators',
        tabId: 'qr',
        path: '/apps/generators/qr',
        keywords: ['qr', 'wifi', 'vcard', 'sms', 'decode'],
        aliases: ['qr code maker', 'qr decoder'],
        icon: 'dx-icon-photo',
        status: 'ready',
        sensitiveInput: true,
        supportsDownload: true
      })
    ]
  },
  {
    id: 'color',
    name: 'Color & Accessibility',
    description: 'Inspect colors, contrast, palettes, and WCAG accessibility.',
    path: '/apps/color',
    icon: 'dx-icon-palette',
    minRole: 'AssistantAdmin',
    status: 'ready',
    tools: [
      localTool({
        id: 'color-palette',
        name: 'Palette Generator',
        shortName: 'Palette',
        description: 'Generate, lock, preview, and export color palettes.',
        hubId: 'color',
        tabId: 'palette',
        path: '/apps/color/palette',
        keywords: ['complementary', 'analogous', 'triadic', 'tetradic', 'shades'],
        aliases: ['color scheme generator'],
        icon: 'dx-icon-palette',
        status: 'ready',
        supportsDownload: true
      }),
      localTool({
        id: 'color-picker',
        name: 'Color Picker',
        shortName: 'Picker',
        description: 'Parse, normalize, and copy HEX, RGB, HSL, HSV, and alpha colors.',
        hubId: 'color',
        tabId: 'picker',
        path: '/apps/color/picker',
        keywords: ['hex', 'rgb', 'hsl', 'hsv', 'alpha'],
        aliases: ['color converter'],
        icon: 'dx-icon-palette',
        status: 'ready'
      }),
      localTool({
        id: 'color-contrast',
        name: 'Contrast Checker',
        shortName: 'Contrast',
        description: 'Check labeled text and UI contrast thresholds.',
        hubId: 'color',
        tabId: 'contrast',
        path: '/apps/color/contrast',
        keywords: ['wcag', 'contrast', 'foreground', 'background', 'accessibility'],
        aliases: ['color contrast'],
        icon: 'dx-icon-eyeopen',
        status: 'ready'
      })
    ]
  }
];

export const STANDALONE_APP_REGISTRY: readonly StandaloneAppDefinition[] = [
  {
    id: 'emoji-keyboard',
    name: 'Emoji & Symbols',
    description: 'Search emoji, Unicode symbols, and ASCII art to copy.',
    path: '/apps/emoji-keyboard',
    icon: 'dx-icon-favorites',
    minRole: 'AssistantUser',
    keywords: ['emoji', 'symbols', 'ascii art', 'unicode', 'accessible name', 'html entity'],
    aliases: ['emoji keyboard', 'symbol picker', 'accessible symbols', 'character names'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'text-editor',
    name: 'Text Editor',
    description: 'Create a plain text, encrypted, log, or env file in My Storage and open it in Workspace.',
    path: '/apps/text-editor',
    icon: 'dx-icon-textdocument',
    minRole: 'AssistantUser',
    keywords: ['text', 'monaco', 'notes', 'code', 'plain text', 'editor'],
    aliases: ['text editor', 'monaco', 'notes.txt', 'plain text editor'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'code-editor',
    name: 'Code Editor',
    description: 'Create a code or config file in My Storage and open it in Workspace.',
    path: '/apps/code-editor',
    icon: 'dx-icon-codeblock',
    minRole: 'AssistantUser',
    keywords: [
      'code',
      'editor',
      'ide',
      'javascript',
      'typescript',
      'python',
      'csharp',
      'json',
      'yaml',
      'xml',
      'sql',
      'powershell'
    ],
    aliases: ['code editor', 'ide'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'markdown-editor',
    name: 'Markdown Editor',
    description: 'Create a blank markdown file in My Storage and open it in Workspace.',
    path: '/apps/markdown-editor',
    icon: 'dx-icon-docfile',
    minRole: 'AssistantUser',
    keywords: ['markdown', 'md', 'notes', 'readme', 'editor'],
    aliases: ['markdown editor', 'md editor', 'notes.md'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'drawio-editor',
    name: 'Draw.io Editor',
    description: 'Create a blank diagram in My Storage and open it in Workspace.',
    path: '/apps/drawio',
    icon: 'dx-icon-hierarchy',
    minRole: 'AssistantUser',
    keywords: ['drawio', 'diagram', 'flowchart', 'uml', 'architecture'],
    aliases: ['draw.io', 'draw.io editor', 'diagram editor', 'flowchart'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'word-doc',
    name: 'Document Editor',
    description: 'Create a blank document in My Storage and open it in Workspace.',
    path: '/apps/word',
    icon: 'dx-icon-docxfile',
    minRole: 'AssistantUser',
    keywords: ['word', 'document', 'docx', 'office', 'richedit'],
    aliases: ['word processor', 'doc editor', 'new document', 'word'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'excel-workbook',
    name: 'Spreadsheet Editor',
    description: 'Create a blank spreadsheet in My Storage and open it in Workspace.',
    path: '/apps/excel',
    icon: 'dx-icon-xlsxfile',
    minRole: 'AssistantUser',
    keywords: ['excel', 'spreadsheet', 'xlsx', 'office', 'workbook'],
    aliases: ['spreadsheet editor', 'new workbook', 'excel'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'pdf-viewer',
    name: 'PDF Viewer',
    description: 'Open a PDF from storage and read, search, or annotate it in Workspace.',
    path: '/apps/pdf-viewer',
    icon: 'dx-icon-pdffile',
    minRole: 'AssistantUser',
    keywords: ['pdf', 'viewer', 'read', 'annotate', 'acrobat', 'document'],
    aliases: ['pdf viewer', 'open pdf', 'read pdf', 'pdf reader'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'analytics-library',
    name: 'Analytics Library',
    description: 'Open published dashboards and reports you are authorized to view.',
    path: '/apps/analytics/library',
    icon: 'dx-icon-folder',
    minRole: 'AssistantUser',
    keywords: ['analytics', 'library', 'dashboard', 'report', 'published', 'viewer'],
    aliases: ['analytics library', 'published dashboards', 'published reports'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'cursor-usage',
    name: 'Cursor Usage',
    description: 'Import Cursor usage CSV and explore personal rollups.',
    path: '/apps/cursor-usage',
    icon: 'dx-icon-chart',
    minRole: 'AssistantAdmin',
    keywords: ['cursor', 'ai usage', 'tokens', 'cost', 'models'],
    aliases: ['cursor analytics', 'cursor spend'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'media-library',
    name: 'Media Library',
    description: 'Browse curated GIFs and memes, and manage your private uploads.',
    path: '/apps/media-library',
    icon: 'dx-icon-image',
    minRole: 'AssistantAdmin',
    keywords: ['gif', 'meme', 'media', 'uploads', 'images', 'stickers'],
    aliases: ['gifs', 'memes', 'image library', 'gif library'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'data-sources',
    name: 'Data Sources',
    description: 'Register storage files and database connections, then preview, import, or query them into Analytics.',
    path: '/apps/analytics/data-sources',
    icon: 'dx-icon-datatrending',
    minRole: 'AssistantAdmin',
    keywords: ['analytics', 'source', 'csv', 'import', 'mongodb', 'sql', 'connection', 'warehouse'],
    aliases: ['data sources', 'file import', 'database connection'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'data-workspace',
    name: 'Data Workspace',
    description: 'Model authorized sources with XYFlow and materialize imported datasets.',
    path: '/apps/analytics/workspace',
    icon: 'dx-icon-hierarchy',
    minRole: 'AssistantAdmin',
    keywords: ['analytics', 'dataset', 'xyflow', 'recipe', 'materialize', 'modeler'],
    aliases: ['data modeler', 'analytics workspace', 'transformation modeler'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'dashboard-builder',
    name: 'Dashboard Builder',
    description: 'Author DevExpress dashboards against imported warehouse datasets.',
    path: '/apps/analytics/dashboards',
    icon: 'dx-icon-chart',
    minRole: 'AssistantAdmin',
    keywords: ['analytics', 'dashboard', 'devexpress', 'designer', 'chart'],
    aliases: ['dashboard designer', 'analytics dashboards', 'dx dashboard'],
    supportsFavorites: true,
    status: 'ready'
  },
  {
    id: 'report-builder',
    name: 'Report Builder',
    description: 'Author DevExpress reports against imported warehouse datasets.',
    path: '/apps/analytics/reports',
    icon: 'dx-icon-textdocument',
    minRole: 'AssistantAdmin',
    keywords: ['analytics', 'report', 'devexpress', 'designer', 'export', 'repx'],
    aliases: ['report designer', 'analytics reports', 'dx report'],
    supportsFavorites: true,
    status: 'ready'
  }
];
